// Single source of truth for /tools/mortgage-preapproval-meeting.

const SITE = "https://www.homeapproach.com";

export const PAGE_PATH = "/tools/mortgage-preapproval-meeting";
export const PAGE_URL = `${SITE}${PAGE_PATH}`;
export const PAGE_TITLE =
  "What to Expect at a Mortgage Pre-Approval Meeting (First-Time Buyer Guide)";
export const PAGE_DESCRIPTION =
  "What happens at a mortgage pre-approval meeting: the documents to bring, how the credit pull and DTI review work, the questions to ask your lender, the timeline, and your next steps as a first-time buyer.";

export const HERO_PROMISE =
  "A pre-approval meeting is a working session, not an interview you can fail. A loan officer collects your documents, pulls your credit, checks how your debts compare to your income, and tells you what a lender is realistically willing to lend. It usually takes 30 to 60 minutes, and most of the outcome is decided by what you bring with you.";

export const HERO_NEXT_STEPS = [
  "Gather the documents in the checklist below — most delays are missing paperwork, not weak finances.",
  "Book the meeting, then leave your credit alone: no new cards, no car loan, no large unexplained deposits.",
];

export const SECTIONS = [
  { id: "prequal-vs-preapproval", title: "Pre-qualification vs pre-approval" },
  { id: "purpose", title: "What the meeting is actually for" },
  { id: "documents", title: "Documents to bring" },
  { id: "steps", title: "Step-by-step: how the meeting runs" },
  { id: "questions", title: "Questions to ask your lender" },
  { id: "mistakes", title: "What not to do afterward" },
  { id: "faq", title: "Common questions" },
  { id: "next-steps", title: "Your next steps" },
];

export const COMPLIANCE_NOTE =
  "Required documents, timing, rate options, and underwriting conditions vary by lender, loan type, and borrower profile. This guide is lender-neutral and describes what is typical, not what any specific lender will require of you. Ask your loan officer for their own document list before your meeting.";

export const DISCLAIMER =
  "Educational information only, not lending, legal, or tax advice. Home Approach is not a lender or mortgage broker. Confirm every requirement and figure directly with a licensed loan officer.";

export interface CompareRow {
  id: string;
  factor: string;
  prequal: string;
  preapproval: string;
}

export const PREQUAL_VS_PREAPPROVAL: CompareRow[] = [
  {
    id: "verification",
    factor: "How your finances are checked",
    prequal: "Based on numbers you state — income, debts, and assets you say you have. Usually nothing is verified.",
    preapproval: "Based on documents you provide: pay stubs, tax returns, and bank statements a lender reviews.",
  },
  {
    id: "credit",
    factor: "Credit check",
    prequal: "Often a soft pull, or no pull at all.",
    preapproval: "Typically a hard credit pull from one or more bureaus, with your written authorization.",
  },
  {
    id: "time",
    factor: "How long it takes",
    prequal: "Minutes, often online with no human involved.",
    preapproval: "A 30 to 60 minute meeting plus lender review, commonly same-day to a few business days.",
  },
  {
    id: "weight",
    factor: "What it means to a seller",
    prequal: "Little. Many listing agents treat a pre-qualification as an estimate.",
    preapproval: "Meaningful. A pre-approval letter is what most agents expect attached to an offer.",
  },
  {
    id: "output",
    factor: "What you walk away with",
    prequal: "A ballpark price range.",
    preapproval: "A pre-approval letter with a loan amount, loan type, and expiration date, plus any conditions.",
  },
];

export interface DocGroup {
  id: string;
  title: string;
  note: string;
  items: string[];
}

export const DOCUMENT_GROUPS: DocGroup[] = [
  {
    id: "identity",
    title: "Identity",
    note: "Bring these for every borrower on the loan.",
    items: [
      "Government-issued photo ID (driver's license or passport)",
      "Social Security number for the credit pull authorization",
      "Current address history, typically the past two years",
    ],
  },
  {
    id: "income",
    title: "Income",
    note: "Lenders want to see income that is stable and likely to continue.",
    items: [
      "Pay stubs covering the last 30 days",
      "W-2 forms for the last two years",
      "Federal tax returns for the last two years, all pages and schedules",
      "Documentation for bonus, overtime, or commission income if you rely on it",
      "Award letters for Social Security, pension, disability, or child support income you want counted",
    ],
  },
  {
    id: "assets",
    title: "Assets and down payment",
    note: "The goal is proving where your cash came from, not just that it exists.",
    items: [
      "Two months of statements for every checking and savings account, all pages",
      "Recent retirement or brokerage statements if you plan to use those funds",
      "An explanation and paper trail for any large or unusual deposit",
      "A gift letter and the donor's transfer record if part of your down payment is a gift",
    ],
  },
  {
    id: "debts",
    title: "Debts and obligations",
    note: "Most of this shows on your credit report, but bring anything that will not.",
    items: [
      "Student loan, auto loan, and credit card statements",
      "Current rent amount and your landlord's contact information",
      "Divorce decree or child support and alimony orders, if applicable",
      "Bankruptcy discharge papers or foreclosure documentation, if applicable",
    ],
  },
  {
    id: "special",
    title: "If your situation is less standard",
    note: "Self-employed, newly employed, or non-citizen borrowers should expect extra requests.",
    items: [
      "Self-employed: two years of business tax returns, a year-to-date profit and loss statement, and 1099s",
      "New job: your signed offer letter and start date",
      "Rental property owner: leases and the related tax schedules",
      "Non-permanent resident: visa or work authorization documentation",
      "Veteran or active duty: your Certificate of Eligibility or DD-214 for a VA loan",
    ],
  },
];

export interface MeetingStep {
  id: string;
  title: string;
  body: string;
  duration: string;
}

export const MEETING_STEPS: MeetingStep[] = [
  {
    id: "prepare",
    title: "Hand over your documents",
    duration: "5–10 minutes",
    body:
      "The loan officer confirms your identity, collects the paperwork, and fills in the application. Anything missing becomes a follow-up item that delays your letter, so it is worth arriving over-prepared.",
  },
  {
    id: "credit",
    title: "Authorize the credit pull",
    duration: "5 minutes",
    body:
      "You give written permission and the lender pulls your credit, usually a hard inquiry from all three bureaus. Mortgage lenders often use older scoring models than the free score in your banking app, so the number they see may differ from the one you expect. Multiple mortgage inquiries within a short shopping window are generally treated as a single event by scoring models.",
  },
  {
    id: "dti",
    title: "Review income, debts, and DTI",
    duration: "10–15 minutes",
    body:
      "The loan officer adds up your monthly debt payments plus the projected housing payment and divides by your gross monthly income. That ratio drives how much they can lend. Ask them to say your debt-to-income ratio out loud and to show which debts they counted.",
  },
  {
    id: "red-flags",
    title: "Talk through anything that needs explaining",
    duration: "5–10 minutes",
    body:
      "Job gaps, a recent move, collections, a large deposit, a co-signed loan you do not pay. Bring these up yourself. Underwriting finds them anyway, and an explanation you volunteer is far easier to document than one discovered later.",
  },
  {
    id: "questions",
    title: "Ask your questions",
    duration: "10 minutes",
    body:
      "This is where loan type, rate options, estimated closing costs, and down payment assistance come up. Use the question list below so you leave with numbers instead of impressions.",
  },
  {
    id: "next-steps",
    title: "Get your letter and your conditions",
    duration: "Same day to a few days",
    body:
      "You should receive a pre-approval letter stating a loan amount, loan type, and expiration date, plus a written list of conditions the lender still needs. Ask when the letter will arrive and whether it can be reissued at a lower amount for a specific offer.",
  },
];

export interface LenderQuestion {
  id: string;
  q: string;
  a: string;
}

export const LENDER_QUESTIONS: LenderQuestion[] = [
  {
    id: "loan-type",
    q: "Which loan types do I qualify for, and which do you recommend for me?",
    a: "Conventional, FHA, VA, and USDA loans have different credit, down payment, and mortgage insurance rules. A good answer names two or three you qualify for and explains the trade-off between them for your situation, not just the one the lender prefers to write.",
  },
  {
    id: "down-payment",
    q: "What is the lowest down payment I can make, and what does that cost me monthly?",
    a: "A lower down payment usually means mortgage insurance. Ask for the monthly payment at two or three down payment levels so you can see the actual difference rather than a percentage.",
  },
  {
    id: "dti-number",
    q: "What is my debt-to-income ratio, and what is your maximum?",
    a: "You want the number and the ceiling. If you are close to the limit, ask which single debt payoff would move you the most — that is often a more efficient use of cash than adding to your down payment.",
  },
  {
    id: "closing-costs",
    q: "What are my estimated closing costs, in dollars?",
    a: "Ask for a written Loan Estimate rather than a verbal range. It breaks out lender fees, third-party services, and prepaid items, and it is the document you use to compare lenders side by side.",
  },
  {
    id: "assistance",
    q: "Do you participate in down payment assistance or first-time buyer programs?",
    a: "Many state housing agency programs only work through approved lenders. If the answer is no, that alone may be a reason to also talk to a lender that does participate.",
  },
  {
    id: "rate-lock",
    q: "When can I lock a rate, and how long does the lock last?",
    a: "Rate locks generally begin once you have a property under contract, not at pre-approval. Ask about lock lengths, what an extension costs, and whether a float-down option exists.",
  },
  {
    id: "letter",
    q: "How long is my pre-approval letter valid, and what would invalidate it?",
    a: "Most letters run 60 to 90 days because credit reports and income documents age out. A job change, new debt, or a large credit score drop can invalidate it earlier.",
  },
  {
    id: "conditions",
    q: "What conditions are still outstanding on my file?",
    a: "Ask for the list in writing. Clearing conditions early is the single biggest thing you control in keeping a later closing on schedule.",
  },
  {
    id: "contact",
    q: "Who do I contact once I am under contract, and how fast do you respond?",
    a: "Offers move in hours. Find out whether you get the loan officer directly, a processor, or a general queue, and confirm weekend availability.",
  },
];

export interface Mistake {
  id: string;
  title: string;
  body: string;
}

export const POST_MEETING_MISTAKES: Mistake[] = [
  {
    id: "new-debt",
    title: "Do not open new credit or finance anything",
    body:
      "A car loan, a furniture plan, or a new card changes your debt-to-income ratio and your score. Lenders commonly re-check credit right before closing, and a new account discovered then can shrink or void your approval.",
  },
  {
    id: "job",
    title: "Do not change jobs without asking first",
    body:
      "Not every job change is disqualifying, but a switch to commission, contract, or self-employed income often is at this stage. Call your loan officer before you accept anything.",
  },
  {
    id: "deposits",
    title: "Do not make large unexplained deposits",
    body:
      "Cash and untraceable transfers are the hardest funds to document. Any money going toward your purchase should have a paper trail, and gifts need a proper gift letter.",
  },
  {
    id: "close-cards",
    title: "Do not close old credit cards",
    body:
      "Closing accounts can shorten your credit history and raise your utilization, which is the opposite of what helps. Leave your accounts as they are until after closing.",
  },
  {
    id: "max-out",
    title: "Do not treat the maximum as your budget",
    body:
      "Approval is based on gross income and ignores childcare, commuting, retirement savings, and maintenance. Decide your own comfortable payment before you start touring homes.",
  },
  {
    id: "one-lender",
    title: "Do not stop at one lender",
    body:
      "Comparing Loan Estimates from a few lenders is how you find out whether your fees and rate are competitive. Mortgage inquiries made within a short shopping window are generally counted once by scoring models.",
  },
];

export interface Faq {
  id: string;
  q: string;
  a: string;
}

export const FAQS: Faq[] = [
  {
    id: "validity",
    q: "How long does a mortgage pre-approval last?",
    a: "Most pre-approval letters are valid for 60 to 90 days, because the credit report and income documents behind them go stale. Renewing is usually simple — updated pay stubs, updated bank statements, and often a fresh credit pull. If your search runs long, ask your lender to refresh the letter rather than letting it lapse mid-offer.",
  },
  {
    id: "rate-lock",
    q: "Can I lock my interest rate at pre-approval?",
    a: "Generally no. Rate locks are usually tied to a specific property and an accepted contract, so the rate quoted at pre-approval is an estimate that moves with the market. Ask your lender what lock terms they offer, how long a standard lock runs, what an extension costs, and whether they offer a float-down if rates fall before closing.",
  },
  {
    id: "closing-costs",
    q: "Will I find out my estimated closing costs at the meeting?",
    a: "You should get an estimate, and once you have applied you are entitled to a written Loan Estimate that breaks costs into lender fees, services you can shop for, and prepaid items like taxes and insurance. Closing costs commonly run a few percent of the purchase price, but the range is wide by state and loan type — use the written figures, not a verbal average.",
  },
  {
    id: "gift-funds",
    q: "Can I use gift funds for my down payment?",
    a: "Often yes, though the rules vary by loan type and lender. Expect to provide a signed gift letter stating the money is a gift with no repayment expected, plus a paper trail showing the transfer from the donor's account to yours. Deposit gift money well before your meeting when you can, and never take it in cash.",
  },
  {
    id: "new-debt",
    q: "Why does new debt after pre-approval matter so much?",
    a: "Pre-approval is a snapshot of your credit and your debt-to-income ratio on a given day. Lenders typically re-verify both shortly before closing. A new monthly payment raises your DTI and can push you past the lender's ceiling, which may reduce your loan amount or cancel the approval entirely — sometimes days before your closing date.",
  },
  {
    id: "denied",
    q: "What happens if I am not approved?",
    a: "You should get specific reasons: credit score, debt-to-income ratio, insufficient documented income, or thin savings. Each has a path forward, and a HUD-approved housing counselor will help you build one at no cost. Many buyers who are declined once are approved within six to twelve months after targeted work on one or two of those factors.",
  },
];

const HOW_TO_JSON_LD = {
  "@type": "HowTo",
  name: "What happens at a mortgage pre-approval meeting",
  description:
    "The six stages of a first-time buyer's mortgage pre-approval meeting, from handing over documents to receiving your pre-approval letter.",
  totalTime: "PT1H",
  step: MEETING_STEPS.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.title,
    text: s.body,
    url: `${PAGE_URL}#steps`,
  })),
};

export const MORTGAGE_PREAPPROVAL_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    HOW_TO_JSON_LD,
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
